import { PackageSearch } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { IoIosSearch } from "react-icons/io";
import { FaStar, FaRegStar } from "react-icons/fa";
import PageHeader from "../../components/AdminSideComponents/common/PageHeader";
import Pagination from "../../components/AdminSideComponents/common/Pagination";
import api from "../../configs/api";
import useDebounce from "../../Hooks/useDebounce";
import { useEntityModal } from "../../Hooks/useEntityModal";
import { confirmAction, showError, showSuccess } from "../../utils/toastUtils";
import CreateReviewModal from "../../components/AdminSideComponents/review/Createreviewmodal";
import EditReviewModal from "../../components/AdminSideComponents/review/Editreviewmodal";

/* Star Rating Display */
const StarRating = ({ rating, size = 14 }) => {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) =>
        i < rating ? (
          <FaStar key={i} size={size} className="text-yellow-400" />
        ) : (
          <FaRegStar key={i} size={size} className="text-yellow-300/50" />
        )
      )}
    </div>
  );
};

/* Review Card */
const ReviewCard = ({ review, onEdit, onDelete }) => {
  return (
    <div
      className="group border border-borderLight/60 bg-surfaceColor overflow-hidden relative flex flex-col
      hover:shadow-[0_12px_28px_rgba(0,0,0,0.12)] 
      transition-all duration-200"
    >
      {/* TOP COLOR ACCENT by rating */}
      <div
        className="h-1 w-full flex-shrink-0"
        style={{
          background:
            review.rating >= 4
              ? "linear-gradient(90deg,#facc15,#fbbf24)"
              : review.rating === 3
              ? "linear-gradient(90deg,#94a3b8,#cbd5e1)"
              : "linear-gradient(90deg,#f87171,#ef4444)",
        }}
      />

      {/* CONTENT */}
      <div className="p-4 flex-1 flex flex-col gap-3 cursor-pointer">
        {/* RATING STARS */}
        <StarRating rating={review.rating} />

        {/* DESCRIPTION */}
        <p className="text-xs text-descriptionText line-clamp-4 leading-relaxed flex-1">
          "{review.description}"
        </p>

        {/* DIVIDER */}
        <div className="border-t border-borderLight/40" />

        {/* NAME + BADGE */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-headingText leading-snug truncate">
            {review.name}
          </h3>
          <span
            className={`text-[10px] font-medium px-2 py-0.5 border ${
              review.status === "active"
                ? "bg-green-50 text-green-600 border-green-200"
                : "bg-red-50 text-red-400 border-red-200"
            }`}
          >
            {review.status}
          </span>
        </div>
      </div>

      {/* HOVER ACTION OVERLAY */}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-end gap-3 h-12 px-3 bg-gradient-to-l from-black/50 via-black/10 to-transparent border-t border-white/10 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-300 ease-in-out">
        <button
          title="Edit"
          onClick={() => onEdit(review._id)}
          className="flex items-center justify-center w-8 h-8 bg-white/20 hover:bg-white/30 text-white border border-white/20 transition"
        >
          <FiEdit size={14} />
        </button>
        <button
          title="Delete"
          onClick={() => onDelete(review)}
          className="flex items-center justify-center w-8 h-8 bg-red-500/70 hover:bg-red-500 text-white border border-red-400/40 transition"
        >
          <FiTrash2 size={14} />
        </button>
      </div>
    </div>
  );
};

/* Main Page */
const AdminReview = () => {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);

  const limit = 8;
  const debouncedSearch = useDebounce(searchInput, 350);
  const reviewModal = useEntityModal();
  const abortRef = useRef(null);

  const fetchReviews = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      const res = await api.get(
        `/reviews?admin=true&page=${page}&limit=${limit}&search=${debouncedSearch}`,
        { signal: controller.signal }
      );
      const data = res.data.data;
      // Handle both { reviews, pagination } and flat array responses
      const reviewList = Array.isArray(data) ? data : (data?.reviews ?? []);
      const paginationData = Array.isArray(data) ? null : (data?.pagination ?? null);
      setReviews(reviewList);
      setPagination(paginationData);
    } catch (error) {
      if (error.name !== "CanceledError" && error.name !== "AbortError") {
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchReviews();
    return () => abortRef.current?.abort();
  }, [fetchReviews]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleEdit = useCallback((reviewId) => {
    reviewModal.openEdit(reviewId);
  }, []);

  const handleReviewCreated = useCallback((newReview) => {
    setReviews((prev) => [newReview, ...prev]);
    if (pagination) {
      setPagination((prev) => ({ ...prev, total: prev.total + 1 }));
    }
  }, [pagination]);

  const handleReviewUpdated = useCallback((updatedReview) => {
    setReviews((prev) =>
      prev.map((r) => (r._id === updatedReview._id ? { ...r, ...updatedReview } : r))
    );
  }, []);

  const handleDelete = useCallback(
    async (review) => {
      const confirmed = await confirmAction({
        title: "Delete Review?",
        text: `This will permanently delete the review by "${review.name}".`,
        confirmText: "Delete",
        cancelText: "Cancel",
      });

      if (!confirmed) return;

      const previousReviews = reviews;

      try {
        setReviews((prev) => prev.filter((r) => r._id !== review._id));
        if (pagination?.total) {
          setPagination((prev) => ({ ...prev, total: prev.total - 1 }));
        }
        await api.delete(`/reviews/${review._id}`);
        showSuccess("Review deleted successfully");
      } catch (error) {
        console.error(error);
        setReviews(previousReviews);
        showError("Failed to delete review");
      }
    },
    [reviews, pagination]
  );

  const total = pagination?.total ?? null;

  return (
    <div className="p-6">
      {/* PAGE HEADER */}
      <PageHeader
        title="Reviews"
        breadcrumb="Dashboard / Reviews"
        customSection={
          <>
            {total != null && (
              <span className="text-xs font-medium text-descriptionText border border-borderLight px-2.5 py-1 bg-surfaceColor">
                {total} {total === 1 ? "review" : "reviews"}
              </span>
            )}
          </>
        }
      />

      {/* ACTION BAR */}
      <div className="flex items-center justify-between mb-6 gap-3">
        {/* SEARCH */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-descriptionText pointer-events-none">
            <IoIosSearch className="size-4" />
          </span>
          <input
            type="text"
            placeholder="Search reviews…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-[42px] w-[25rem] pl-9 pr-4 border border-borderLight outline-none focus:border-primaryAccent text-sm bg-white transition-colors"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-descriptionText hover:text-headingText text-xs"
            >
              ✕
            </button>
          )}
        </div>

        {/* RIGHT CONTROLS */}
        <div className="flex items-center gap-2">
          <button
            onClick={reviewModal.openCreate}
            className="h-[42px] px-5 bg-btnColor text-white text-sm font-medium tracking-wide hover:bg-btnHoverColor transition-colors"
          >
            + Create Review
          </button>
        </div>
      </div>

      {/* GRID or SKELETON */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {loading ? (
          Array.from({ length: limit }).map((_, i) => <SkeletonCard key={i} />)
        ) : !loading && reviews?.length === 0 ? (
          <EmptyState search={debouncedSearch} onOpen={reviewModal.openCreate} />
        ) : (
          reviews?.map((review) => (
            <ReviewCard
              key={review._id}
              review={review}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      {/* PAGINATION */}
      {!loading && (
        <Pagination
          pagination={pagination}
          page={page}
          onPageChange={setPage}
        />
      )}

      {/* MODALS */}
      <CreateReviewModal modal={reviewModal} onReviewCreated={handleReviewCreated} />
      <EditReviewModal modal={reviewModal} onReviewUpdated={handleReviewUpdated} />
    </div>
  );
};

/* Empty State */
const EmptyState = ({ search, onOpen }) => (
  <div className="col-span-full flex items-center justify-center py-24">
    <div className="max-w-sm w-full border border-dashed border-borderLight bg-surfaceAccentLight/40 rounded-xl p-10 flex flex-col items-center text-center">
      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primaryAccent/10 text-primaryAccent mb-4">
        <PackageSearch size={28} />
      </div>
      <p className="text-lg font-semibold text-headingText">No reviews found</p>
      <p className="text-sm text-descriptionText mt-2 leading-relaxed">
        {search
          ? `No results for "${search}". Try using a different keyword.`
          : "You haven't added any reviews yet. Start by creating your first review."}
      </p>
      {!search && (
        <button
          className="mt-5 px-5 py-2 bg-btnColor text-white text-sm font-medium rounded-md hover:bg-btnHoverColor transition"
          onClick={onOpen}
        >
          + Create Review
        </button>
      )}
    </div>
  </div>
);

/* Skeleton Card */
const SkeletonCard = () => (
  <div className="border border-borderLight bg-surfaceColor overflow-hidden animate-pulse">
    <div className="h-1 bg-divider" />
    <div className="p-4 space-y-3">
      <div className="flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="w-3.5 h-3.5 bg-divider rounded-sm" />
        ))}
      </div>
      <div className="space-y-1.5">
        <div className="h-3 bg-divider rounded w-full" />
        <div className="h-3 bg-divider rounded w-5/6" />
        <div className="h-3 bg-divider rounded w-4/6" />
        <div className="h-3 bg-divider rounded w-3/6" />
      </div>
      <div className="border-t border-borderLight/40 pt-3 flex items-center justify-between">
        <div className="h-3 bg-divider rounded w-1/3" />
        <div className="h-4 bg-divider rounded w-12" />
      </div>
    </div>
  </div>
);

export default AdminReview;